import { ModalRouteService } from './../services/modal-route.service';
import { Publication } from './../interfaces/publication';
import { PublicationService } from './../services/publication.service';
import { Component, isDevMode, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgRedux, DevToolsExtension } from '@angular-redux/store';
import { Reducer } from 'redux';
import { IAppState, rootReducer } from './../shared/redux/app.store';
import { UploadService } from '../services/upload.service';
import { Router, NavigationEnd, Route, Event } from '@angular/router';
import { WorklistService, Worklist } from '../services/worklist.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'bb-ovp6',
	template: `
		<div [class]="class" [class.with-modal-open]="modalRouteService.modalOpen">
			<bb-navigation *ngIf="currentRouterPath && configLoaded" [publicationConfig]="publicationConfig"
				[hidden]="currentRouterPath && isLoginPage && currentRouterPath?.includes('login')"
			></bb-navigation>
			<router-outlet></router-outlet>
			<div class="route-modal" *ngIf="modalRouteService?.modalOpen">
				<div class="modal-backdrop" (click)="modalRouteService.close()"></div>
				<div class="modal-inner has-header has-sidebar has-buttonbar">
					<router-outlet name="modal"></router-outlet>
				</div>
			</div>
			<bb-worklist></bb-worklist>
			<bb-upload-status-box
				[attr.data-file-count]="uploadService.getVisibleCount()"
				[hidden]="uploadService.getVisibleCount() === 0"
			></bb-upload-status-box>
			<bb-overlay></bb-overlay>
		</div>
	`,
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

	class = '';
	isLibraryMenu: boolean;
	globalStyleAppended = false;

	currentRouterPath: string;
	isLoginPage = true;
	configLoaded = false;
	publicationConfig: Publication;

	private reduxStoreReady = false;

	private subscriptions: Subscription;

	constructor(
		private translate: TranslateService,
		private ngRedux: NgRedux<IAppState>,
		private devTools: DevToolsExtension,
		private router: Router,
		private publicationService: PublicationService,
		public modalRouteService: ModalRouteService,
		private worklistService: WorklistService,
		public uploadService: UploadService) {

		this.translate.use('en_US');

		this.subscriptions = new Subscription();

		this.subscriptions.add(
			this.publicationService.getConfig().subscribe((publication: Publication) => {
				this.publicationConfig = publication;
				this.checkNewLoginOverride();

				if (!this.globalStyleAppended && publication && publication.ovpCssOverride) {
					const style = document.createElement('style');
					style.innerHTML = publication.ovpCssOverride;
					document.body.appendChild(style);
					this.globalStyleAppended = true;
				}
				this.configLoaded = true;
			})
		);

		this.subscriptions.add(this.worklistService.getTheWorklist().subscribe((worklist: Worklist) => {
			if (!this.class) {
				this.class = '';
			}
			if (this.isLibraryMenu) {
				let classes = this.class.replace('  ', ' ').split(' ');
				if (worklist.isOpen) {
					classes.push('with-worklist-open');
				} else {
					classes = classes.filter((clazz: string) => clazz !== 'with-worklist-open');
				}
				this.class = classes.join(' ');
			}
		}));

		this.subscriptions.add(this.router.events.subscribe((event: Event) => {
			if (event instanceof NavigationEnd) {
				let eventPaths: string[] = (event.urlAfterRedirects || event.url).replace(/^\//, '').replace(/\/[0-9]+.*$/, '').split('?')[0].split('/');
				if (eventPaths.length) {
					if (eventPaths[eventPaths.length - 1] === 'new') {
						eventPaths = eventPaths.slice(0, -1);
					}
					if (event.urlAfterRedirects.match(/\/(?:(?:[0-9]+)|(?:new-?))/)) {
						eventPaths[eventPaths.length - 1] = eventPaths[eventPaths.length - 1] + '/:id';
					}
					if (event.urlAfterRedirects.match(/\/(?:(?:[0-9]+)|(?:new-?))\//)) {
						eventPaths[eventPaths.length - 1] = eventPaths[eventPaths.length - 1] + '/:tab';
					}
				}
				let routerConfigs: Route[] = this.router.config;
				let currentRoute: Route = null;

				this.isLibraryMenu = false;
				if (event.urlAfterRedirects.startsWith('/library')) {
					this.isLibraryMenu = true;
				}

				eventPaths.forEach((path: string) => {
					for (let i = 0; i < routerConfigs.length; i++) {
						const route: Route = routerConfigs[i];
						if (route.path === path) {
							currentRoute = route;
							if (route.children && route.children.length) {
								routerConfigs = route.children;
							}
							break;
						}
					}
				});
				if (currentRoute) {
					this.currentRouterPath = currentRoute.path;
					let classes = [];
					if (currentRoute.data && currentRoute.data.class) {
						classes = currentRoute.data.class.replace('  ', ' ').split(' ');
					} else {
						classes = ['has-header', 'has-sidebar', 'has-buttonbar'];
					}
					if (this.isLibraryMenu) {
						if (this.worklistService.worklist && this.worklistService.worklist.isOpen) {
							classes.push('with-worklist-open');
						}
					}

					if (!this.checkNewLoginOverride()) {
						this.class = classes.join(' ');
					}
				}
			}
		}));
	}

	ngOnInit() {
		if (!this.reduxStoreReady) {
			try {
				this.ngRedux.getState();
			} catch {
				const enhancers = [];
				if (isDevMode()) {
					const enhancer = this.devTools.enhancer();
					if (enhancer) {
						enhancers.push(enhancer);
					}
				}

				const r: Reducer = rootReducer;
				this.ngRedux.configureStore(r, undefined, [], enhancers);
				this.reduxStoreReady = true;
			}
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	private checkNewLoginOverride(): boolean {
		// If new branding: do not show bb-navigation for login page. Default hide bb-navigation first, to avoid flash of content on new login page
		// note: reset-password is part of /login but has own routing
		if (!['login', 'reset-password'].includes(this.currentRouterPath)) {
			this.isLoginPage = false;
			return false;
		} else {
			this.isLoginPage = true;
			this.class = 'new-login';
			return true;
		}
	}
}
